import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Search, X, Menu, User, LogOut } from 'lucide-react';
import { useEditable } from '../context/EditableContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const Header = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  const { siteConfig, navigationItems } = useEditable();
  const { user, isAuthenticated, logout } = useAuth();

  const handleDropdownToggle = (id) => {
    setOpenDropdown(openDropdown === id ? null : id);
  };

  const isActive = (href) => location.pathname === href;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#3d4f5a]/95 backdrop-blur-sm">
      <div className="flex items-center justify-between px-4 lg:px-8 py-3">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 text-white text-lg font-light tracking-wide hover:opacity-80 transition-opacity">
          <img 
            src="https://customer-assets.emergentagent.com/job_laikos-school/artifacts/tkonto33_IMG_1836.jpeg"
            alt="Laikos Logo"
            className="w-8 h-8 object-contain rounded-full"
          />
          {siteConfig.siteName}
        </Link>

        {/* Mobile Menu Button */}
        <button 
          className="lg:hidden text-white p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
          {navigationItems.map((item) => (
            <div key={item.id} className="relative">
              {item.hasDropdown ? (
                <button
                  onClick={() => handleDropdownToggle(item.id)}
                  className={`flex items-center gap-1 px-4 py-2 text-sm text-white hover:bg-white/10 rounded transition-colors ${
                    isActive(item.href) ? 'bg-white/10' : ''
                  }`}
                >
                  {item.label}
                  <ChevronDown size={16} className={`transition-transform ${openDropdown === item.id ? 'rotate-180' : ''}`} />
                </button>
              ) : (
                <Link
                  to={item.href}
                  className={`px-4 py-2 text-sm text-white hover:bg-white/10 rounded transition-colors block ${
                    isActive(item.href) ? 'bg-white/10' : ''
                  }`}
                >
                  {item.label}
                </Link>
              )}

              {/* Dropdown Menu */}
              {item.hasDropdown && openDropdown === item.id && (
                <div className="absolute top-full left-0 mt-1 bg-white rounded shadow-lg min-w-48 py-2 z-50">
                  {item.dropdownItems.map((dropItem) => (
                    <Link
                      key={dropItem.id}
                      to={dropItem.href}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setOpenDropdown(null)}
                    >
                      {dropItem.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Search Button */}
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="p-2 text-white hover:bg-white/10 rounded transition-colors"
          >
            <Search size={20} />
          </button>

          {/* Auth Buttons */}
          <div className="flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-white hover:bg-white/10 rounded transition-colors"
                >
                  <User size={16} />
                  <span className="max-w-24 truncate">{user?.name}</span>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10"
                  onClick={handleLogout}
                >
                  <LogOut size={16} />
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-white text-[#3d4f5a] hover:bg-gray-100">
                    Register
                  </Button>
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <nav className="lg:hidden bg-[#3d4f5a] border-t border-white/10 px-4 py-4">
          {navigationItems.map((item) => (
            <div key={item.id}>
              {item.hasDropdown ? (
                <>
                  <button
                    onClick={() => handleDropdownToggle(item.id)}
                    className="flex items-center justify-between w-full px-4 py-3 text-white hover:bg-white/10 rounded"
                  >
                    {item.label}
                    <ChevronDown size={16} className={`transition-transform ${openDropdown === item.id ? 'rotate-180' : ''}`} />
                  </button>
                  {openDropdown === item.id && (
                    <div className="pl-4">
                      {item.dropdownItems.map((dropItem) => (
                        <Link
                          key={dropItem.id}
                          to={dropItem.href}
                          className="block px-4 py-2 text-sm text-white/80 hover:text-white hover:bg-white/5 rounded"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {dropItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.href}
                  className="block px-4 py-3 text-white hover:bg-white/10 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}

          {/* Mobile Auth Links */}
          <div className="mt-4 pt-4 border-t border-white/10">
            {isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  className="block px-4 py-3 text-white hover:bg-white/10 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User size={16} className="inline mr-2" /> Dashboard
                </Link>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="block w-full text-left px-4 py-3 text-white hover:bg-white/10 rounded"
                >
                  <LogOut size={16} className="inline mr-2" /> Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block px-4 py-3 text-white hover:bg-white/10 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="block px-4 py-3 text-white bg-white/10 hover:bg-white/20 rounded mt-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Register
                </Link>
              </>
            )}
          </div>
        </nav>
      )}

      {/* Search Overlay */}
      {searchOpen && (
        <div className="absolute top-full left-0 right-0 bg-white shadow-lg p-4">
          <div className="max-w-2xl mx-auto flex items-center gap-2">
            <Search size={20} className="text-gray-400" />
            <input
              type="text"
              placeholder="Search this site..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 outline-none text-gray-700 text-lg"
              autoFocus
            />
            <button onClick={() => setSearchOpen(false)} className="p-2 hover:bg-gray-100 rounded">
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {openDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setOpenDropdown(null)}
        />
      )}
    </header>
  );
};

export default Header;
